import toast, { Toaster } from 'react-hot-toast';
import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CheckCircle } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // ✅ Tikhat Foods support details
  const SUPPORT_EMAIL = 'hello@tikhat.in';
  const SUPPORT_PHONE_1 = '+91 91403 17009';
  const SUPPORT_PHONE_2 = '+91 88407 37660';

  // ✅ Auto-fill referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (registrationSuccess) {
      toast.success('Account created — pending approval');
    }
  }, [registrationSuccess]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const phoneRegex = /^[+]?[0-9]{10,13}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    const result = await register({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      referralCode: formData.referralCode || undefined,
    });

    if (result.success || result?.error?.toLowerCase().includes('pending')) {
      setRegistrationSuccess(true);
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <Toaster position="top-right" />
      <div className="w-full max-w-2xl">

        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/logo.png"
            alt="Tikhat Partner"
            className="h-24 object-contain"
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">

          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
            Become a Tikhat Foods Partner
          </h2>
          <p className="text-gray-600 mb-6">
            Start your grocery business journey with Tikhat Foods
          </p>

          {registrationSuccess ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-success-light rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-success" />
              </div>

              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                Registration Successful
              </h3>

              <p className="text-gray-700 mb-6">
                Your partner account has been created and is currently
                <strong> pending verification</strong>.
                You will be notified once approval is completed.
              </p>

              {/* 📞 SUPPORT SECTION */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Tikhat Foods Support
                </h4>
                <p className="text-sm text-gray-700">
                  📞 {SUPPORT_PHONE_1}
                </p>
                <p className="text-sm text-gray-700">
                  📞 {SUPPORT_PHONE_2}
                </p>
                <p className="text-sm text-gray-700">
                  📧 {SUPPORT_EMAIL}
                </p>
              </div>

              <div className="flex justify-center gap-3">
                <Link
                  to="/login"
                  className="bg-primary text-white font-semibold py-3 px-6 rounded-md"
                >
                  Go to Login
                </Link>
                <button
                  onClick={() => navigate('/')}
                  className="border border-gray-300 bg-white text-gray-800 font-semibold py-3 px-6 rounded-md"
                >
                  Back to Home
                </button>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-error-light border border-error text-error-dark px-4 py-3 rounded-md mb-4">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Full Name"
                    className="w-full px-4 py-3 border rounded-md"
                  />
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 border rounded-md"
                  />
                </div>

                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email Address"
                  className="w-full px-4 py-3 border rounded-md"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Password"
                    className="w-full px-4 py-3 border rounded-md"
                  />
                  <input
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Confirm Password"
                    className="w-full px-4 py-3 border rounded-md"
                  />
                </div>

                <input
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  placeholder="Referral Code (Optional)"
                  className="w-full px-4 py-3 border rounded-md"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white font-semibold py-3 rounded-md"
                >
                  {loading ? 'Creating Account…' : 'Create Partner Account'}
                </button>
              </form>

              <p className="text-sm text-center text-gray-600 mt-6">
                Already a partner?{' '}
                <Link to="/login" className="text-primary font-semibold">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;